import { useEffect, useRef, useState } from "react";
import { t } from "../../i18n";
import {
  CheckCircle2,
  Circle,
  ListTodo,
  LoaderCircle,
  Play,
} from "lucide-react";
import { api } from "../../api";
import { AgentIcon } from "./AgentIcon";
import { Markdown } from "../Markdown";
import { CompletedMessageActivity } from "../chat/CompletedMessageActivity";
import { ActivityEventCard } from "../chat/ActivityEventCard";
import { ThinkingBlock } from "../chat/ThinkingBlock";
import { useInteractionDisclosure } from "../chat/MessageActivity.utils";
import type {
  ApprovalHandler,
  QuestionAnswerHandler,
} from "../chat/MessageActivity";
import {
  latestTodoList,
  type AgentWorkspaceApproval,
  type SubagentRun,
  type SubagentRunView,
} from "@lumen/shared";

export function SubagentDiscussion({
  run,
  approvals,
  busy,
  decidingApprovalIds,
  onApproval,
  onQuestionAnswer,
  onSubagentContinue,
}: {
  run: SubagentRunView;
  approvals: Map<string, AgentWorkspaceApproval>;
  busy: boolean;
  decidingApprovalIds: Set<string>;
  onApproval?: ApprovalHandler;
  onQuestionAnswer?: QuestionAnswerHandler;
  onSubagentContinue?: () => void;
}) {
  const [loadedRun, setLoadedRun] = useState<SubagentRun | null>(() =>
    hasSubagentTranscript(run) ? run : null,
  );
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [transcriptError, setTranscriptError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const currentRun = useRef(run);
  currentRun.current = run;
  const hasLiveTranscript = hasSubagentTranscript(run);
  const transcriptRun = hasLiveTranscript
    ? run
    : loadedRun?.id === run.id
      ? { ...loadedRun, ...run }
      : null;
  const task =
    !hasLiveTranscript && loadedRun?.id === run.id ? loadedRun.task : run.task;
  const toolEvents = transcriptRun?.toolEvents ?? [];
  const activityEvents = transcriptRun?.activityEvents ?? [];
  const approvalEvents = toolEvents.filter(
    (event) => event.status === "awaiting-approval" && event.approvalId,
  );
  const hasPendingApproval = approvalEvents.some((event) => {
    const approval = approvals.get(event.approvalId!);
    // An approval not present in the asynchronously loaded map is still
    // unknown, not resolved. Keep Continue hidden until the server confirms
    // that it is no longer pending.
    return !approval || approval.status === "pending";
  });
  const canContinueSubagent = Boolean(
    onSubagentContinue &&
    !busy &&
    (run.status !== "awaiting-approval" || Boolean(transcriptRun)) &&
    (run.status !== "awaiting-approval" || !hasPendingApproval),
  );
  const questionAnswerHandler =
    run.status === "awaiting-question" ? onQuestionAnswer : undefined;
  const modelLabel = run.modelName || run.modelRemoteId;
  const waitingForInteraction =
    run.status === "awaiting-approval" || run.status === "awaiting-question";
  // Completed helpers stay available on demand without overwhelming the
  // parent turn's final answer. Active work and pending interactions stay open.
  const { open, onToggle } = useInteractionDisclosure(
    run.status === "running" || waitingForInteraction,
  );

  useEffect(() => {
    if (hasLiveTranscript) setLoadedRun(run);
  }, [hasLiveTranscript, run]);

  useEffect(() => {
    if (!open || hasLiveTranscript || loadedRun?.id === run.id) return;
    const controller = new AbortController();
    let active = true;
    setTranscriptLoading(true);
    setTranscriptError(false);
    api<SubagentRun>(
      `/conversations/${encodeURIComponent(run.conversationId)}/subagent-runs/${encodeURIComponent(run.id)}`,
      { signal: controller.signal, notifyDataChange: false },
    )
      .then((detail) => {
        if (!active) return;
        setLoadedRun((current) => {
          const latest = currentRun.current;
          if (hasSubagentTranscript(latest)) return latest;
          if (
            current?.id === detail.id &&
            new Date(current.updatedAt).getTime() >
              new Date(detail.updatedAt).getTime()
          )
            return current;
          return detail;
        });
      })
      .catch((error: unknown) => {
        if (
          active &&
          !(error instanceof DOMException && error.name === "AbortError")
        )
          setTranscriptError(true);
      })
      .finally(() => {
        if (active) setTranscriptLoading(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [
    hasLiveTranscript,
    loadedRun?.id,
    open,
    retryCount,
    run.conversationId,
    run.id,
  ]);

  const todoList = open ? latestTodoList([{ toolEvents }]) : null;
  const hasTextActivity = open
    ? activityEvents.some((event) => event.type === "text")
    : false;
  const hasReasoningActivity = open
    ? activityEvents.some((event) => event.type === "reasoning")
    : false;
  return (
    <>
      <details
        data-subagent-run={run.id}
        open={open}
        onToggle={onToggle}
        className="overflow-hidden rounded-xl border border-violet-200 bg-violet-50/40 text-xs dark:border-violet-900/60 dark:bg-violet-950/20"
      >
        <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2.5 marker:content-none">
          <AgentIcon
            name={run.agentIcon}
            size={16}
            className="shrink-0 text-violet-600 dark:text-violet-300"
          />
          <strong className="text-stone-700 dark:text-stone-200">
            {run.agentName}
          </strong>
          {modelLabel && (
            <span className="shrink-0 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-700 dark:bg-violet-950/60 dark:text-violet-300">
              {modelLabel}
            </span>
          )}
          <span className="truncate text-stone-400">{task}</span>
          <span
            className={`ml-auto shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${run.status === "completed" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300" : run.status === "failed" ? "bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-300" : "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300"}`}
          >
            {run.status === "awaiting-approval"
              ? "approval needed"
              : run.status === "awaiting-question"
                ? "answer needed"
                : run.status}
          </span>
        </summary>
        {open && (
          <div className="space-y-3 border-t border-violet-200 p-3 dark:border-violet-900/60">
            <details>
              <summary className="cursor-pointer text-stone-400">
                {t("Delegated task")}
              </summary>
              <div className="rounded-lg bg-white/70 p-2.5 text-sm leading-5 whitespace-pre-wrap text-stone-600 dark:bg-black/10 dark:text-stone-300">
                {task}
              </div>
            </details>
            {!transcriptRun && (
              <div
                className="flex items-center gap-2 text-stone-400"
                role="status"
              >
                {!transcriptError && (
                  <LoaderCircle className="animate-spin" size={13} />
                )}
                {transcriptError ? (
                  <>
                    <span>{t("Could not load subagent activity")}</span>
                    <button
                      type="button"
                      className="font-semibold text-violet-700 dark:text-violet-300"
                      onClick={() => setRetryCount((count) => count + 1)}
                    >
                      {t("Retry")}
                    </button>
                  </>
                ) : transcriptLoading ? (
                  t("Loading subagent activity…")
                ) : (
                  t("Starting subdiscussion…")
                )}
              </div>
            )}
            {transcriptRun && run.status === "completed" ? (
              <CompletedMessageActivity
                message={{
                  id: run.id,
                  conversationId: run.conversationId,
                  parentId: null,
                  versionGroupId: run.id,
                  versionIndex: 1,
                  role: "assistant",
                  createdAt: run.createdAt,
                  generationStatus: "completed",
                  attachments: [],
                  content: transcriptRun.content,
                  reasoning: transcriptRun.reasoning,
                  toolEvents,
                  activityEvents,
                }}
              />
            ) : (
              transcriptRun && (
                <>
                  {todoList && (
                    <div className="rounded-lg border border-violet-200 bg-white/70 p-3 dark:border-violet-900/60 dark:bg-black/10">
                      <div className="mb-2 flex items-center gap-2 font-semibold">
                        <ListTodo
                          size={14}
                          className="text-violet-600 dark:text-violet-300"
                        />
                        {t("Subagent tasks")}
                        <span className="ml-auto text-[10px] text-stone-400">
                          {todoList.completed}/{todoList.total}
                        </span>
                      </div>
                      <ol className="space-y-2">
                        {todoList.todos.map((todo, index) => (
                          <li
                            key={`${index}-${todo.content}`}
                            className={`flex items-start gap-2 ${todo.status === "completed" ? "text-stone-400" : "text-stone-600 dark:text-stone-300"}`}
                          >
                            {todo.status === "completed" ? (
                              <CheckCircle2
                                size={14}
                                className="mt-0.5 shrink-0 text-emerald-600"
                              />
                            ) : todo.status === "in_progress" ? (
                              <LoaderCircle
                                size={14}
                                className="mt-0.5 shrink-0 animate-spin text-violet-600"
                              />
                            ) : (
                              <Circle
                                size={14}
                                className="mt-0.5 shrink-0 text-stone-300"
                              />
                            )}
                            <span
                              className={
                                todo.status === "completed"
                                  ? "line-through"
                                  : ""
                              }
                            >
                              {todo.content}
                            </span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                  {activityEvents.map((event) => {
                    return (
                      <ActivityEventCard
                        key={event.id}
                        event={event}
                        busy={busy}
                        streaming={run.status === "running"}
                        compact
                        approvals={approvals}
                        decidingApprovalIds={decidingApprovalIds}
                        onApproval={onApproval}
                        onQuestionAnswer={questionAnswerHandler}
                        onContinue={
                          canContinueSubagent ? undefined : onSubagentContinue
                        }
                      />
                    );
                  })}
                  {!hasReasoningActivity && transcriptRun.reasoning && (
                    <ThinkingBlock
                      content={transcriptRun.reasoning}
                      streaming={run.status === "running"}
                      label={t("Subagent thinking")}
                      compact
                    />
                  )}
                  {!hasTextActivity && transcriptRun.content && (
                    <div className="rounded-lg bg-white/70 p-3 text-sm dark:bg-black/10">
                      <Markdown streaming={run.status === "running"}>
                        {transcriptRun.content}
                      </Markdown>
                    </div>
                  )}
                  {transcriptRun.error && (
                    <div className="rounded-lg bg-red-50 p-3 text-red-700 dark:bg-red-950/30 dark:text-red-300">
                      {transcriptRun.error}
                    </div>
                  )}
                  {run.status === "running" && !activityEvents.length && (
                    <span className="inline-flex items-center gap-2 text-stone-400">
                      <LoaderCircle className="animate-spin" size={13} />
                      {t("Starting subdiscussion…")}
                    </span>
                  )}
                </>
              )
            )}
          </div>
        )}
      </details>
      {canContinueSubagent && (
        <div className="flex justify-end pt-1">
          <button
            type="button"
            className="button gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-violet-700 dark:text-violet-300"
            onClick={onSubagentContinue}
            title={t("Continue the subagent")}
          >
            <Play size={13} /> {t("Continue")}
          </button>
        </div>
      )}
    </>
  );
}

function hasSubagentTranscript(run: SubagentRunView): run is SubagentRun {
  return (
    typeof run.content === "string" &&
    (typeof run.reasoning === "string" || run.reasoning === null) &&
    Array.isArray(run.toolEvents) &&
    Array.isArray(run.activityEvents)
  );
}
